import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme, type ThemeMode } from '../contexts/ThemeContext';

const OPTIONS: { value: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

interface ThemeSwitcherProps {
  /** Compact inline variant (icon + label row) vs. full card grid */
  variant?: 'grid' | 'inline';
}

export function ThemeSwitcher({ variant = 'grid' }: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme();

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-elevated)] p-1">
        {OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            title={label}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
              theme === value
                ? 'bg-[var(--color-primary)] text-white shadow-sm'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setTheme(value)}
          className={`flex min-h-20 flex-col items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-all duration-150 ${
            theme === value
              ? 'border-[var(--color-primary)] bg-[var(--color-surface-subtle)] text-[var(--color-text-primary)]'
              : 'border-[var(--color-border-subtle)] bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-subtle)]'
          }`}
        >
          <Icon
            className={`h-5 w-5 ${
              theme === value ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-light)]'
            }`}
          />
          {label}
        </button>
      ))}
    </div>
  );
}
